import { addDays, weekdayOf } from '@shh/shared';
import { cn, Heading, Surface } from '@shh/ui';
import { Button as AriaButton } from 'react-aria-components';
import { addMonths, calendarDay, type Layers } from '../../data/calendar';
import type { E } from '../../data/derive';
import type { FamilyData } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { useSwipePane } from '../../lib/useSwipePane';
import { dayShort, monthName } from './format';

/** Plans of the family – what makes a day "busy" (not bins, school or holidays). */
const planned = (e: E) => !e.virtual && e.source !== 'waste';

/**
 * The year at a glance (1.24): twelve months as a heat map, the next free weekends and what is
 * coming (holidays, birthdays) – planning far ahead, the way families ask ("wann haben wir frei?").
 */
export function YearView({
  f,
  date,
  layers,
  people,
  onMonth,
  onWeekend,
  onOpen,
  onStep,
}: {
  f: FamilyData;
  date: string;
  layers: Layers;
  people?: string[];
  onMonth: (first: string) => void;
  onWeekend: (saturday: string) => void;
  onOpen: (id: string, date: string) => void;
  onStep: (dir: 1 | -1) => void;
}) {
  const year = date.slice(0, 4);
  const { ref, style } = useSwipePane<HTMLDivElement>(onStep);
  const day = (d: string) => calendarDay(f, d, { layers, people });

  const months = Array.from(
    { length: 12 },
    (_, m) => `${year}-${String(m + 1).padStart(2, '0')}-01`,
  );
  const from = year === f.today.slice(0, 4) ? f.today : `${year}-01-01`;

  // the next weekends with nothing planned on Saturday and/or Sunday
  const weekends: { sat: string; both: boolean; satFree: boolean }[] = [];
  for (
    let s = addDays(from, (6 - weekdayOf(from) + 7) % 7), i = 0;
    i < 60 && weekends.length < 5;
    i++, s = addDays(s, 7)
  ) {
    const satFree = !day(s).some(planned);
    const sunFree = !day(addDays(s, 1)).some(planned);
    if (satFree || sunFree) weekends.push({ sat: s, both: satFree && sunFree, satFree });
  }
  // holidays and birthdays ahead
  const soon: { e: E; date: string }[] = [];
  const seen = new Set<string>();
  for (let d = from, i = 0; i < 366 && soon.length < 5; i++, d = addDays(d, 1)) {
    for (const e of day(d)) {
      if (
        (e.virtual === 'birthday' || (e.virtual === 'holiday' && !e.publicHoliday)) &&
        !seen.has(e.id)
      ) {
        seen.add(e.id);
        soon.push({ e, date: d });
      }
    }
  }

  return (
    <div ref={ref} style={style} className="flex min-h-svh touch-pan-y flex-col gap-section">
      <div className="grid grid-cols-3 gap-gap-sm md:grid-cols-4 xl:grid-cols-6">
        {months.map((m) => {
          const len = Number(addDays(addMonths(m, 1), -1).slice(8));
          return (
            <AriaButton
              key={m}
              aria-label={calendarText.openMonth(monthName(m))}
              onPress={() => onMonth(m)}
              className="flex cursor-pointer flex-col gap-1 rounded-card bg-surface-raised p-inset-xs text-left outline-none data-hovered:shadow-raised data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
            >
              <span
                className={cn(
                  'text-sm font-black',
                  m.slice(0, 7) === f.today.slice(0, 7) && 'text-feedback-danger',
                )}
              >
                {monthName(m)}
              </span>
              <span aria-hidden className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: weekdayOf(m) - 1 }, (_, i) => (
                  <span key={`x${i}`} />
                ))}
                {Array.from({ length: len }, (_, i) => {
                  const d = addDays(m, i);
                  const list = day(d);
                  const n = list.filter(planned).length;
                  const hol = list.some((e) => e.virtual === 'holiday');
                  return (
                    <span
                      key={d}
                      className={cn(
                        'aspect-square',
                        n >= 3
                          ? 'bg-accent-primary'
                          : n === 2
                            ? 'bg-accent-primary-soft'
                            : n === 1
                              ? 'bg-accent-secondary-soft'
                              : hol
                                ? 'bg-feedback-warning-surface'
                                : 'bg-surface-sunken',
                        d === f.today && 'ring-2 ring-feedback-danger',
                      )}
                    />
                  );
                })}
              </span>
            </AriaButton>
          );
        })}
      </div>
      <p className="text-sm text-text-muted">{calendarText.yearHint}</p>

      <div className="grid grid-cols-1 gap-section md:grid-cols-2">
        <section aria-labelledby="free-weekends" className="flex flex-col gap-gap-sm">
          <Heading level={2} size="md" id="free-weekends">
            {calendarText.freeWeekends}
          </Heading>
          <p className="text-sm text-text-secondary">{calendarText.freeWeekendsHint}</p>
          {weekends.map((w) => (
            <AriaButton
              key={w.sat}
              onPress={() => onWeekend(w.sat)}
              className="flex cursor-pointer flex-col rounded-card bg-surface-raised p-inset-sm text-left outline-none data-hovered:shadow-raised data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
            >
              <span className="font-bold">
                {w.both
                  ? calendarText.weekendFree
                  : w.satFree
                    ? calendarText.saturdayFree
                    : calendarText.sundayFree}
              </span>
              <span className="text-sm text-text-secondary">
                {dayShort(w.sat)} – {dayShort(addDays(w.sat, 1))}
              </span>
            </AriaButton>
          ))}
        </section>
        <section aria-labelledby="soon" className="flex flex-col gap-gap-sm">
          <Heading level={2} size="md" id="soon">
            {calendarText.soon}
          </Heading>
          {soon.length ? (
            soon.map(({ e, date: d }) => (
              <AriaButton
                key={e.id}
                onPress={() => onOpen(e.id, d)}
                className={cn(
                  'flex cursor-pointer flex-col rounded-card p-inset-sm text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
                  e.virtual === 'birthday'
                    ? 'bg-accent-tertiary-soft'
                    : 'bg-feedback-warning-surface',
                )}
              >
                <span className="font-bold">{e.title}</span>
                <span className="text-sm text-text-secondary">
                  {dayShort(d)}
                  {e.endDate && e.endDate !== d ? ` – ${dayShort(e.endDate)}` : ''}
                </span>
              </AriaButton>
            ))
          ) : (
            <Surface tone="sunken" className="text-sm">
              {calendarText.noEvents}
            </Surface>
          )}
        </section>
      </div>
    </div>
  );
}
